import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Thermometer, Droplets, Wind, Gauge, Eye, Sun, ChevronDown, ChevronUp,
  Snowflake, AlertTriangle, Shield, Car, Compass, Activity
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function ExpandToggle({ open }) {
  return open ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />;
}

function DetailRow({ label, value, sub, color }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-xs text-white/40">{label}</span>
      <div className="text-right">
        <span className={`text-sm font-semibold ${color || ""}`}>{value}</span>
        {sub && <span className="text-[10px] text-white/25 ml-1">{sub}</span>}
      </div>
    </div>
  );
}

function SourceBadge({ source }) {
  if (!source) return null;
  return (
    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#00f2ea]/10 text-[#00f2ea]/60">
      {source}
    </span>
  );
}

function SourceMatchBadge({ comparison, testId }) {
  if (!comparison) return null;
  const label = comparison.agreement === "Single source" ? "Single-source" : `${comparison.agreement} match`;
  const tone = comparison.agreement === "Mixed" ? "text-amber-300 bg-amber-400/10 border-amber-300/20" : "text-cyan-300 bg-cyan-300/10 border-cyan-300/20";
  return (
    <span className={`inline-flex max-w-full rounded-md border px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${tone}`} data-testid={testId}>
      {label}
    </span>
  );
}

export function EnhancedWeatherCards({ lat, lon, isDarkMode }) {
  const [data, setData] = useState(null);
  const [pressure, setPressure] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchEnhanced = useCallback(async () => {
    if (!lat || !lon) return;
    try {
      const [currentRes, pressureRes] = await Promise.all([
        axios.get(`${API}/api/weather/enhanced/current`, { params: { lat, lon } }).catch(() => null),
        axios.get(`${API}/api/weather/enhanced/pressure-history`, { params: { lat, lon } }).catch(() => null),
      ]);
      if (currentRes?.data) setData(currentRes.data);
      if (pressureRes?.data) setPressure(pressureRes.data);
    } catch (err) {
      console.error("Enhanced weather error:", err.message);
    } finally {
      setLoading(false);
    }
  }, [lat, lon]);

  useEffect(() => { fetchEnhanced(); }, [fetchEnhanced]);

  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  if (loading || !data) return null;

  const temp = data.temperature || {};
  const wind = data.wind || {};
  const pres = data.pressure || {};
  const hum = data.humidity || {};
  const uv = data.uv || {};
  const vis = data.visibility || {};
  const sources = data.sources || {};
  const feels = temp.feels_like || {};

  const cardClass = `rounded-2xl p-3 sm:p-4 min-h-[150px] sm:min-h-[166px] cursor-pointer transition-[transform,background-color,border-color,box-shadow] duration-200 active:scale-[0.99] ${isDarkMode ? "bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.07]" : "bg-white border border-gray-200 hover:shadow-md"}`;
  const titleClass = "text-[11px] sm:text-xs font-bold text-white/55 leading-tight break-words";
  const valueClass = "text-2xl sm:text-[1.7rem] font-black leading-none tracking-normal";

  return (
    <div className="grid grid-cols-1 min-[360px]:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="enhanced-weather-cards">
      {/* Temperature Card */}
      <button type="button" className={`${cardClass} text-left`} onClick={() => toggle("temp")} data-testid="temp-card-enhanced" aria-expanded={expanded.temp}>
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-start gap-1.5 min-w-0">
            <Thermometer className="w-4 h-4 text-[#ff6b6b]" />
            <span className={titleClass}>Temperature</span>
          </div>
          <ExpandToggle open={expanded.temp} />
        </div>
        <div className={valueClass}>{temp.value}°F</div>
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <span className="text-[10px] text-white/30">Feels {feels.feels_like}°F</span>
          <SourceBadge source={temp.source === "NWS Observation" ? "NWS" : "OWM"} />
        </div>
        <AnimatePresence>
          {expanded.temp && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden mt-3 pt-3 border-t border-white/5">
              <DetailRow label="High / Low" value={`${temp.high}° / ${temp.low}°F`} />
              <DetailRow label="Feels Like" value={`${feels.feels_like}°F`} sub={feels.method === "wind_chill" ? "Wind Chill" : feels.method === "heat_index" ? "Heat Index" : "Actual"} />
              {feels.wind_chill && <DetailRow label="Wind Chill" value={`${feels.wind_chill}°F`} color="text-cyan-400" />}
              {feels.heat_index && <DetailRow label="Heat Index" value={`${feels.heat_index}°F`} color="text-red-400" />}
              <DetailRow label="Dew Point" value={hum.dew_point_f ? `${hum.dew_point_f}°F` : "N/A"} />
              {sources.agreement && (
                <div className={`text-[10px] mt-2 p-2 rounded-lg ${sources.agreement.status === "agree" ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"}`}>
                  {sources.agreement.message}
                </div>
              )}
              <div className="text-[10px] text-white/20 mt-1">{sources.nws_station ? `Station: ${sources.nws_station}` : "Source: OpenWeatherMap"}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </button>

      {/* Wind Card */}
      <button type="button" className={`${cardClass} text-left`} onClick={() => toggle("wind")} data-testid="wind-card-enhanced" aria-expanded={expanded.wind}>
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-start gap-1.5 min-w-0">
            <Wind className="w-4 h-4 text-[#00f2ea]" />
            <span className={titleClass}>Wind</span>
          </div>
          <ExpandToggle open={expanded.wind} />
        </div>
        <div className={valueClass}>{wind.speed_mph}<span className="text-sm font-normal text-white/40"> mph</span></div>
        <span className="mt-2 block text-[10px] text-white/30 leading-tight">{wind.direction_compass} — {wind.beaufort?.name}</span>
        <div className="mt-2"><SourceMatchBadge comparison={wind.source_comparison} testId="wind-source-comparison-badge" /></div>
        <AnimatePresence>
          {expanded.wind && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden mt-3 pt-3 border-t border-white/5">
              <DetailRow label="Direction" value={`${wind.direction_compass} (${wind.direction_deg}°)`} />
              <DetailRow label="Gusts" value={wind.gust_mph ? `${wind.gust_mph} mph` : "None"} color={wind.gust_mph > 25 ? "text-red-400" : ""} />
              <DetailRow label="Beaufort" value={`${wind.beaufort?.number} — ${wind.beaufort?.name}`} />
              {wind.source_comparison && <DetailRow label="Source Diff" value={`${wind.source_comparison.difference_mph} mph`} sub="NWS vs OWM" color={wind.source_comparison.agreement === "Mixed" ? "text-amber-400" : "text-cyan-300"} />}
              <p className="text-[10px] text-white/25 mt-1">{wind.beaufort?.description}</p>
              {/* Wind compass */}
              <div className="flex justify-center mt-3">
                <div className="relative w-16 h-16">
                  <div className="absolute inset-0 rounded-full border border-white/10" />
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 text-[8px] text-white/30">N</div>
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-[8px] text-white/30">S</div>
                  <div className="absolute top-1/2 left-0 -translate-y-1/2 text-[8px] text-white/30">W</div>
                  <div className="absolute top-1/2 right-0 -translate-y-1/2 text-[8px] text-white/30">E</div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                    style={{ transform: `translate(-50%, -50%) rotate(${wind.direction_deg || 0}deg)` }}>
                    <Compass className="w-5 h-5 text-[#00f2ea]" />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </button>

      {/* Pressure Card */}
      <button type="button" className={`${cardClass} text-left`} onClick={() => toggle("pressure")} data-testid="pressure-card-enhanced" aria-expanded={expanded.pressure}>
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-start gap-1.5 min-w-0">
            <Gauge className="w-4 h-4 text-[#a78bfa]" />
            <span className={titleClass}>Pressure</span>
          </div>
          <ExpandToggle open={expanded.pressure} />
        </div>
        <div className={valueClass}>{pres.inhg}<span className="text-sm font-normal text-white/40"> inHg</span></div>
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${pres.trend === "rising" || pres.trend === "slowly_rising" ? "bg-emerald-500/15 text-emerald-400" : pres.trend === "falling" || pres.trend === "slowly_falling" ? "bg-red-500/15 text-red-400" : "bg-white/10 text-white/40"}`}>
            {pres.trend === "rising" ? "Rising" : pres.trend === "slowly_rising" ? "Slowly Rising" : pres.trend === "falling" ? "Falling" : pres.trend === "slowly_falling" ? "Slowly Falling" : "Steady"}
          </span>
          <SourceMatchBadge comparison={pres.source_comparison} testId="pressure-source-comparison-badge" />
        </div>
        <AnimatePresence>
          {expanded.pressure && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden mt-3 pt-3 border-t border-white/5">
              <DetailRow label="hPa" value={`${pres.hpa} hPa`} />
              {pres.source_comparison && <DetailRow label="Source Diff" value={`${pres.source_comparison.difference_hpa} hPa`} sub="NWS vs OWM" color={pres.source_comparison.agreement === "Mixed" ? "text-amber-400" : "text-cyan-300"} />}
              <DetailRow label="Storm Risk" value={pres.assessment?.storm_risk?.toUpperCase()} color={pres.assessment?.storm_risk === "high" ? "text-red-400" : pres.assessment?.storm_risk === "moderate" ? "text-amber-400" : "text-emerald-400"} />
              <p className="text-[10px] text-white/30 mt-1">{pres.assessment?.state}</p>
              {pressure && (
                <>
                  <DetailRow label="6hr Change" value={`${pressure.change_6h > 0 ? "+" : ""}${pressure.change_6h} hPa`} color={pressure.change_6h < -2 ? "text-red-400" : pressure.change_6h > 2 ? "text-emerald-400" : ""} />
                  {pressure.storm_note && (
                    <div className="flex items-center gap-1 text-[10px] mt-2 p-2 rounded-lg bg-red-500/10 text-red-400">
                      <AlertTriangle className="w-3 h-3 flex-shrink-0" /> {pressure.storm_note}
                    </div>
                  )}
                  {/* Mini pressure chart */}
                  {pressure.history?.length > 6 && (
                    <div className="mt-2 h-10 flex items-end gap-[1px]">
                      {pressure.history.slice(-24).map((h, i) => {
                        const min = Math.min(...pressure.history.slice(-24).map(x => x.hpa));
                        const max = Math.max(...pressure.history.slice(-24).map(x => x.hpa));
                        const range = max - min || 1;
                        const height = ((h.hpa - min) / range) * 100;
                        return <div key={`pbar-${h.time}`} className="flex-1 rounded-t bg-[#a78bfa]/40" style={{ height: `${Math.max(5, height)}%` }} />;
                      })}
                    </div>
                  )}
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </button>

      {/* Humidity Card */}
      <button type="button" className={`${cardClass} text-left`} onClick={() => toggle("humidity")} data-testid="humidity-card-enhanced" aria-expanded={expanded.humidity}>
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-start gap-1.5 min-w-0">
            <Droplets className="w-4 h-4 text-[#60a5fa]" />
            <span className={titleClass}>Humidity</span>
          </div>
          <ExpandToggle open={expanded.humidity} />
        </div>
        <div className={valueClass}>{hum.value}<span className="text-sm font-normal text-white/40">%</span></div>
        <span className="mt-2 block text-[10px] text-white/30 leading-tight">{hum.comfort?.level}</span>
        <AnimatePresence>
          {expanded.humidity && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden mt-3 pt-3 border-t border-white/5">
              <DetailRow label="Dew Point" value={hum.dew_point_f ? `${hum.dew_point_f}°F` : "N/A"} />
              <DetailRow label="Comfort" value={hum.comfort?.level} />
              <p className="text-[10px] text-white/25 mt-1">{hum.comfort?.note}</p>
              <DetailRow label="Frost Risk" value={hum.frost_risk?.toUpperCase()} color={hum.frost_risk === "high" ? "text-cyan-400" : hum.frost_risk === "moderate" ? "text-amber-400" : "text-emerald-400"} />
              <DetailRow label="Mold Risk" value={hum.mold_risk?.toUpperCase()} color={hum.mold_risk === "high" ? "text-red-400" : hum.mold_risk === "moderate" ? "text-amber-400" : "text-emerald-400"} />
            </motion.div>
          )}
        </AnimatePresence>
      </button>

      {/* UV Card */}
      <button type="button" className={`${cardClass} text-left`} onClick={() => toggle("uv")} data-testid="uv-card-enhanced" aria-expanded={expanded.uv}>
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-start gap-1.5 min-w-0">
            <Sun className="w-4 h-4 text-[#fbbf24]" />
            <span className={titleClass}>UV Index</span>
          </div>
          <ExpandToggle open={expanded.uv} />
        </div>
        <div className={valueClass} style={{ color: uv.index <= 2 ? "#4ade80" : uv.index <= 5 ? "#fbbf24" : uv.index <= 7 ? "#f97316" : "#ef4444" }}>
          {uv.index}
        </div>
        <span className="mt-2 block text-[10px] text-white/30 leading-tight">{uv.burn_time?.risk}</span>
        <AnimatePresence>
          {expanded.uv && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden mt-3 pt-3 border-t border-white/5">
              <DetailRow label="Risk Level" value={uv.burn_time?.risk} color={uv.index > 7 ? "text-red-400" : uv.index > 5 ? "text-amber-400" : "text-emerald-400"} />
              {uv.burn_time?.minutes && <DetailRow label="Burn Time" value={`~${uv.burn_time.minutes} min`} sub="(Type II skin)" />}
              <div className="flex items-center gap-1 text-[10px] mt-2 p-2 rounded-lg bg-amber-500/10 text-amber-400">
                <Shield className="w-3 h-3 flex-shrink-0" /> {uv.burn_time?.recommendation}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </button>

      {/* Visibility Card */}
      <button type="button" className={`${cardClass} text-left`} onClick={() => toggle("vis")} data-testid="visibility-card-enhanced" aria-expanded={expanded.vis}>
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-start gap-1.5 min-w-0">
            <Eye className="w-4 h-4 text-white/50" />
            <span className={titleClass}>Visibility</span>
          </div>
          <ExpandToggle open={expanded.vis} />
        </div>
        <div className={valueClass}>{vis.miles}<span className="text-sm font-normal text-white/40"> mi</span></div>
        <span className="mt-2 block text-[10px] text-white/30 leading-tight">{vis.condition}</span>
        <AnimatePresence>
          {expanded.vis && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden mt-3 pt-3 border-t border-white/5">
              <DetailRow label="Condition" value={vis.condition} color={vis.miles < 1 ? "text-red-400" : vis.miles < 3 ? "text-amber-400" : "text-emerald-400"} />
              <DetailRow label="Fog Risk" value={vis.fog_risk?.toUpperCase()} color={vis.fog_risk === "high" ? "text-red-400" : ""} />
              <div className="flex items-center gap-1 text-[10px] mt-2 p-2 rounded-lg bg-white/5">
                <Car className="w-3 h-3 flex-shrink-0 text-white/30" /> {vis.driving}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </button>
    </div>
  );
}
